/**
 * EmployeeChatPanel - a right-side slide-out where the operator converses with
 * one AI employee. At runtime the employee is "Neo with a Voice Profile + a
 * skill in mind": persistent, logged, resumable threads the operator returns to
 * (e.g. a standing Follow-up Specialist thread for neglected leads).
 *
 * Opened app-wide via the `poe:open-employee-chat` CustomEvent (dispatched from
 * the context-zone roster), so it needs no prop drilling through the inbox.
 */
import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Bot, Sparkles, X, Send, Plus, Loader2 } from 'lucide-react';
import { cn } from '../../lib/utils.js';
import {
  useEmployeeConversations,
  useEmployeeMessages,
  useCreateEmployeeConversation,
  useSendEmployeeMessage,
} from '../../lib/employeeChat.js';

export interface EmployeeChatTarget {
  id: string;
  name: string;
  voiceProfileId?: string | null;
}

export default function EmployeeChatPanel({
  employee,
  open,
  onClose,
  width = 420,
  rightOffset = 0,
}: {
  employee: EmployeeChatTarget | null;
  open: boolean;
  onClose: () => void;
  width?: number;
  rightOffset?: number;
}) {
  const reduce = useReducedMotion();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  const convsQuery = useEmployeeConversations(employee?.id, open && !!employee);
  const conversations = convsQuery.data?.conversations ?? [];
  const messagesQuery = useEmployeeMessages(activeId);
  const messages = messagesQuery.data?.messages ?? [];
  const createConv = useCreateEmployeeConversation();
  const send = useSendEmployeeMessage(activeId);

  // Reset when switching employee; auto-select the most recent thread.
  useEffect(() => {
    setActiveId(null);
    setDraft('');
  }, [employee?.id]);
  useEffect(() => {
    const first = conversations[0];
    if (open && employee && activeId === null && first) {
      setActiveId(first.id);
    }
  }, [open, employee, activeId, conversations]);

  // Keep the latest turn in view.
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length, send.isPending]);

  const startConversation = () => {
    if (!employee) return;
    createConv.mutate(employee.id, { onSuccess: (d) => setActiveId(d.conversation.id) });
  };

  const submit = () => {
    const body = draft.trim();
    if (!body || !activeId || send.isPending) return;
    setDraft('');
    send.mutate(body);
  };

  if (!open || !employee) return null;

  return (
    <AnimatePresence>
      <motion.aside
        key={`employee-chat-${employee.id}`}
        className="fixed top-0 z-50 flex h-full max-w-full flex-col border-l border-line bg-surface shadow-2xl"
        style={{ width, right: rightOffset, paddingTop: 'env(safe-area-inset-top, 0px)' }}
        initial={reduce ? false : { x: width + 24, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={reduce ? { opacity: 0 } : { x: width + 24, opacity: 0 }}
        transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
      >
        {/* Header */}
        <div className="flex items-center gap-2 border-b border-line px-4 py-3">
          <Bot size={18} className="shrink-0 text-brand" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span className="truncate text-sm font-semibold text-ink">{employee.name}</span>
              {employee.voiceProfileId ? <Sparkles size={12} className="shrink-0 text-ink-subtle" /> : null}
            </div>
            <span className="text-[11px] text-ink-muted">AI employee</span>
          </div>
          <button
            type="button"
            onClick={startConversation}
            disabled={createConv.isPending}
            title="New conversation"
            className="rounded-md p-1.5 text-ink-muted hover:bg-surface-raised hover:text-ink disabled:opacity-50"
          >
            <Plus size={16} />
          </button>
          <button type="button" onClick={onClose} title="Close" className="rounded-md p-1.5 text-ink-muted hover:bg-surface-raised hover:text-ink">
            <X size={16} />
          </button>
        </div>

        {/* Thread switcher (only when there are multiple) */}
        {conversations.length > 1 ? (
          <div className="border-b border-line px-3 py-2">
            <select
              value={activeId ?? ''}
              onChange={(e) => setActiveId(e.target.value || null)}
              className="w-full rounded-md border border-line bg-surface-raised px-2 py-1 text-xs text-ink"
            >
              {conversations.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
          {!activeId ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
              <p className="max-w-[80%] text-sm text-ink-muted">
                Start a conversation with {employee.name}. This thread is logged and you can return to it anytime.
              </p>
              <button
                type="button"
                onClick={startConversation}
                disabled={createConv.isPending}
                className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
              >
                {createConv.isPending ? 'Starting...' : 'Start conversation'}
              </button>
            </div>
          ) : messages.length === 0 && !messagesQuery.isLoading ? (
            <p className="text-center text-xs text-ink-muted">Say hello, or ask {employee.name} to get to work.</p>
          ) : (
            messages.map((m) => (
              <div key={m.id} className={cn('flex', m.role === 'user' ? 'justify-end' : 'justify-start')}>
                <div
                  className={cn(
                    'max-w-[85%] whitespace-pre-wrap rounded-2xl px-3 py-2 text-sm',
                    m.role === 'user' ? 'bg-brand/10 text-ink' : 'bg-surface-raised text-ink',
                  )}
                >
                  {m.body}
                </div>
              </div>
            ))
          )}
          {send.isPending ? (
            <div className="flex justify-start">
              <div className="flex items-center gap-1.5 rounded-2xl bg-surface-raised px-3 py-2 text-xs text-ink-muted">
                <Loader2 size={12} className="animate-spin" /> {employee.name} is thinking
              </div>
            </div>
          ) : null}
        </div>

        {/* Composer */}
        <div className="border-t border-line p-3">
          <div className="flex items-end gap-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                  e.preventDefault();
                  submit();
                }
              }}
              placeholder={activeId ? `Message ${employee.name}...` : 'Start a conversation first'}
              disabled={!activeId || send.isPending}
              rows={2}
              className="max-h-32 min-h-[2.5rem] flex-1 resize-none rounded-md border border-line bg-surface-raised px-2.5 py-1.5 text-sm text-ink placeholder:text-ink-subtle focus:outline-none focus:ring-1 focus:ring-brand disabled:opacity-50"
            />
            <button
              type="button"
              onClick={submit}
              disabled={!draft.trim() || !activeId || send.isPending}
              title="Send (Cmd/Ctrl + Enter)"
              className="rounded-md bg-brand p-2 text-white hover:opacity-90 disabled:opacity-40"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      </motion.aside>
    </AnimatePresence>
  );
}
